import { MappingApi } from '../../../../../api/mappingApi';
import { TemplatesApi } from '../../../../../api/templatesApi';
import { AdvancedOptions, MappingInfo } from '../../../../../interfaces/Mapping';
import { SnackbarParameters } from '../../../../../interfaces/Snackbar';
import { Template } from '../../../../../interfaces/Templates';
import withSnackbar from '../../../../../shared/components/withSnackbar';
import { DEFAULT_ADVANCED_OPTIONS } from '../../../../../shared/constants/ImportPageConstants';
import {
	SovosButton,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosTextField,
} from 'mosaic-react';
import { useEffect, useState } from 'react';

interface CreateTemplateDialogProps {
	isDialogOpen: boolean;
	onCloseDialog: () => void;
	onClose: (value: Template | undefined) => void;
	mappedColumns: MappingInfo[];
	templateInfo: any;
	forceReloadingTemplate: () => void;
	showSnackbar: (value: SnackbarParameters) => void;
	selectedGlobalTemplate: Template;
}

const CreateTemplateDialog = (props: CreateTemplateDialogProps) => {
	const {
		isDialogOpen,
		onCloseDialog,
		onClose,
		mappedColumns,
		templateInfo,
		forceReloadingTemplate,
		showSnackbar,
		selectedGlobalTemplate,
	} = props;
	const [templateName, setTemplateName] = useState<string>('');
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isTemplateNameDirty, setIsTemplateNameDirty] = useState<boolean>(false);
	const [errorTemplateName, setErrorTemplateName] = useState<string>('');

	useEffect(() => {
		setTemplateName('');
		setIsTemplateNameDirty(false);
	}, [isDialogOpen]);

	const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setTemplateName(event.target.value);
		setErrorTemplateName(!event.target.value ? 'Template Name is required.' : '');
		setIsTemplateNameDirty(true);
	};

	const handleClose = (): void => {
		if (!isSaving) {
			onClose(undefined);
		}
	};

	const createTemplate = (mappingId: string, scope: string): void => {
		const { calculationType, dateFormat, transactionType, ruleType, decimalSymbol, thousandSeparator } =
			selectedGlobalTemplate;
		const advancedOptionsBody: AdvancedOptions = {
			calculationType: calculationType || DEFAULT_ADVANCED_OPTIONS.calculationType,
			dateFormat: dateFormat || DEFAULT_ADVANCED_OPTIONS.dateFormat,
			transactionType: transactionType || DEFAULT_ADVANCED_OPTIONS.transactionType,
			ruleType: ruleType || DEFAULT_ADVANCED_OPTIONS.ruleType,
			decimalSymbol: decimalSymbol || DEFAULT_ADVANCED_OPTIONS.decimalSymbol,
			thousandSeparator: thousandSeparator || DEFAULT_ADVANCED_OPTIONS.thousandSeparator,
		};
		let templateCreationBody = {
			...templateInfo,
			scope,
			mappingId,
			name: templateName,
		};

		if (selectedGlobalTemplate.id !== 'Custom Format') {
			templateCreationBody = { ...templateCreationBody, calculationType, dateFormat, transactionType, ruleType };
		}

		TemplatesApi.createTemplate(templateCreationBody, advancedOptionsBody, mappingId)
			.then(response => {
				if (response && response.data && response.data.errors) {
					setErrorTemplateName(response.data.errors[0].message);
				} else if (response.status === 201) {
					onClose(response.data);
					forceReloadingTemplate();
					showSnackbar({ message: 'New template created', variant: 'confirmation' });
				} else {
					showSnackbar({ message: 'Unable to create template. Please try again later.', variant: 'error' });
					onCloseDialog();
				}
			})
			.catch(error => setErrorTemplateName(error.response.data.errors[0].message))
			.finally(() => setIsSaving(false));
	};

	const handleSave = (): void => {
		setIsTemplateNameDirty(true);
		if (!templateName) {
			setErrorTemplateName('Template Name is required.');
			return;
		}
		if (!errorTemplateName && isDialogOpen) {
			setIsSaving(true);

			MappingApi.saveMappedColumns(templateInfo.calculationType, mappedColumns)
				.then(response => {
					if (response && response.data && response.data.errors) {
						setErrorTemplateName(response.data.errors[0].message);
						setIsSaving(false);
					} else if (response.status === 201) {
						const { id, scope } = response.data;
						createTemplate(id, scope);
					} else {
						showSnackbar({ message: 'Unable to create template. Please try again later.', variant: 'error' });
						onCloseDialog();
						setIsSaving(false);
					}
				})
				.catch(error => console.log(error));
		}
	};

	return (
		<SovosDialog open={isDialogOpen} onClose={handleClose}>
			<SovosDialogTitle className='custom-title'>Save New Template</SovosDialogTitle>
			<SovosDialogContent>
				<p>Name your template below. Once saved, you&lsquo;ll be able to apply this template to future imports.</p>
				<div>
					<SovosTextField
						onChange={handleNameChange}
						placeholder='Template Name'
						value={templateName}
						autoFocus
						fullWidth
						error={isTemplateNameDirty && Boolean(errorTemplateName)}
						helperText={isTemplateNameDirty && errorTemplateName}
						inputProps={{ maxLength: 50 }}
					/>
				</div>
			</SovosDialogContent>
			<SovosDialogActions>
				<SovosButton variant='text' onClick={handleClose} disabled={isSaving}>
					Cancel
				</SovosButton>
				<SovosButton onClick={handleSave} disabled={isSaving}>
					Save Template
				</SovosButton>
			</SovosDialogActions>
		</SovosDialog>
	);
};

export default withSnackbar(CreateTemplateDialog);
